import clsx from 'clsx';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChatMessage } from '../../types/chat';
import { ToolCallIndicator } from './ToolCallIndicator';
import { ConfirmationCard } from './ConfirmationCard';
import { ActionResultCard } from './ActionResultCard';

interface MessageBubbleProps {
  message: ChatMessage;
  onConfirm?: (actionId: string) => void;
  onCancel?: (actionId: string) => void;
  isConfirmLoading?: boolean;
  isConfirmResolved?: boolean;
  isStreaming?: boolean;
}

export const MessageBubble = ({
  message,
  onConfirm,
  onCancel,
  isConfirmLoading,
  isConfirmResolved,
  isStreaming,
}: MessageBubbleProps) => {
  if (message.type === 'tool_call' && message.toolCall) {
    return <ToolCallIndicator toolName={message.toolCall.tool} status={message.toolCall.status} />;
  }

  if (message.type === 'action_result') {
    return <ActionResultCard content={message.content} cards={message.platformCards} />;
  }

  const isUser = message.type === 'user';

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-[rgb(var(--color-primary-rgb))] text-white">
          <p className="whitespace-pre-wrap">{message.content}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'text-sm',
        message.type === 'error'
          ? 'text-red-700 dark:text-red-400'
          : message.type === 'rate_limited'
            ? 'text-gray-500 dark:text-gray-400'
            : 'text-gray-900 dark:text-gray-100'
      )}
    >
      <div className="chat-markdown">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {message.content}
        </ReactMarkdown>
        {isStreaming && (
          <span className="inline-block w-[2px] h-[1em] ml-0.5 bg-current animate-pulse align-middle" />
        )}
      </div>

      {message.type === 'confirmation' && message.confirmation && onConfirm && onCancel && (
        <ConfirmationCard
          confirmation={message.confirmation}
          onConfirm={onConfirm}
          onCancel={onCancel}
          isLoading={isConfirmLoading}
          isResolved={isConfirmResolved}
        />
      )}
    </div>
  );
};
